package io.horion.cv.components.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import io.horion.cv.utils.Colors
import org.jetbrains.compose.web.css.px
import kotlin.js.Date
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.viewModel.CvViewModel
import io.horion.cv.components.widgets.DateRangeSelector
import io.horion.cv.components.widgets.FormActionButtons
import io.horion.cv.components.widgets.RichTextDescription
import io.horion.cv.components.widgets.TextFormField
import io.horion.cv.models.FormationState
import io.horion.cv.styles.ActivityFormStyle
import io.horion.cv.styles.DropdownSelectStyle
import io.horion.cv.styles.FormationFieldStyle
import io.horion.cv.styles.FormationFieldsStyle
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

@Composable
fun FormationForm(
    index: Int,
    state: FormationState,
    cvViewModel: CvViewModel,
    onDelete: () -> Unit,
    onComplete: () -> Unit
) {

    val updateVM = {
        val updatedFormation = io.horion.cv.api.datasource.cv.Formation(
            label = state.formation,
            school = state.etablissement,
            city = state.ville,
            started = "${state.moisDebut}-${state.anneeDebut}",
            ended = if (state.isOngoing) "En cours" else "${state.moisFin}-${state.anneeFin}",
            description = state.description
        )
        cvViewModel.updateFormation(index, updatedFormation)
    }

    Column(
        modifier = ActivityFormStyle.toModifier()
    ) {
        TextFormField(
            label = "Formation",
            value = state.formation,
            onValueChange = {
                state.formation = it
                updateVM()
            },
            modifier = Modifier
                .fillMaxWidth()
                .minWidth(0.px)
        )

        Column(
            modifier = FormationFieldsStyle.toModifier()
        ) {
            TextFormField(
                label = "Établissement",
                value = state.etablissement,
                onValueChange = {
                    state.etablissement = it
                    updateVM()
                },
                modifier = FormationFieldStyle.toModifier()
            )

            TextFormField(
                label = "Ville",
                value = state.ville,
                onValueChange = {
                    state.ville = it
                    updateVM()
                },
                modifier = FormationFieldStyle.toModifier()
            )
        }

        DateRangeSelector(
            startMonth = state.moisDebut,
            startYear = state.anneeDebut,
            endMonth = state.moisFin,
            endYear = state.anneeFin,
            isOngoing = state.isOngoing,
            onStartMonthChange = {
                state.moisDebut = it
                updateVM()
            },
            onStartYearChange = {
                state.anneeDebut = it
                updateVM()
            },
            onEndMonthChange = {
                state.moisFin = it
                updateVM()
            },
            onEndYearChange = {
                state.anneeFin = it
                updateVM()
            },
            onOngoingChange = {
                state.isOngoing = it

                if (it) {
                    state.moisFin = ""
                    state.anneeFin = ""
                }

                updateVM()
            }
        )

        RichTextDescription(
            label = "Description",
            value = state.description,
            onValueChange = {
                state.description = it
                updateVM()
            },
            modifier = Modifier
                .fillMaxWidth()
                .minWidth(0.px)
        )

        FormActionButtons(
            onDelete = onDelete,
            onComplete = onComplete
        )
    }
}

@Composable
fun MonthDropdown(
    selectedMonth: String,
    onMonthChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    disabled: Boolean = false,
) {
    val months = listOf(
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre"
    )

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val selectBg = if (isDark) {
        Colors.input
    } else {
        Colors.LightGray
    }

    val selectColor = if (isDark) {
        Colors.White
    } else {
        Colors.Primary
    }

    Select(
        attrs = DropdownSelectStyle
            .toModifier()
            .then(modifier)
            .then(
                if (disabled) {
                    Modifier
                        .opacity(0.4f)
                        .cursor(Cursor.NotAllowed)
                } else {
                    Modifier
                        .cursor(Cursor.Pointer)
                }
            )
            .toAttrs {
                if (disabled) {
                    disabled()
                }

                style {
                    backgroundColor(selectBg)
                    color(selectColor)
                }

                onChange { event ->
                    if (!disabled) {
                        onMonthChange(event.target.value)
                    }
                }
            }
    ) {
        Option(
            value = "",
            attrs = {
                if (selectedMonth.isEmpty()) {
                    selected()
                }

                style {
                    color(Colors.Primary)
                }
            }
        ) {
            Text("Mois")
        }

        months.forEachIndexed { index, month ->
            val value = (index + 1).toString()

            Option(
                value = value,
                attrs = {
                    if (value == selectedMonth) {
                        selected()
                    }

                    style {
                        backgroundColor(selectBg)
                        color(selectColor)
                    }
                }
            ) {
                Text(month)
            }
        }
    }
}

@Composable
fun YearDropdown(
    selectedYear: String,
    onYearChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    disabled: Boolean = false,
) {
    val currentYear = Date().getFullYear()
    val years = (currentYear downTo 1970).map {
        it.toString()
    }

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val selectBg = if (isDark) {
        Colors.input
    } else {
        Colors.LightGray
    }

    val selectColor = if (isDark) {
        Colors.White
    } else {
        Colors.Primary
    }

    Select(
        attrs = DropdownSelectStyle
            .toModifier()
            .then(modifier)
            .then(
                if (disabled) {
                    Modifier
                        .opacity(0.4f)
                        .cursor(Cursor.NotAllowed)
                } else {
                    Modifier
                        .cursor(Cursor.Pointer)
                }
            )
            .toAttrs {
                if (disabled) {
                    disabled()
                }

                style {
                    backgroundColor(selectBg)
                    color(selectColor)
                }

                onChange { event ->
                    if (!disabled) {
                        onYearChange(event.target.value)
                    }
                }
            }
    ) {
        Option(
            value = "",
            attrs = {
                if (selectedYear.isEmpty()) {
                    selected()
                }

                style {
                    color(Colors.Primary)
                }
            }
        ) {
            Text("Année")
        }

        years.forEach { year ->
            Option(
                value = year,
                attrs = {
                    if (year == selectedYear) {
                        selected()
                    }

                    style {
                        backgroundColor(selectBg)
                        color(selectColor)
                    }
                }
            ) {
                Text(year)
            }
        }
    }
}